'use client'

import {
    HealthPlan,
    getHealthPlans,
    getClientHealthPlans,
} from '@/lib/api/medical/health-plan-api'
import { useAuth } from '@/store/hooks'
import { useCallback, useEffect, useRef, useState } from 'react'
import { MdClose, MdHealthAndSafety, MdSearch } from 'react-icons/md'

interface Props {
    value: number | null
    onChange: (healthPlanId: number | null) => void
    clientId?: number | null
    label?: string
    placeholder?: string
    disabled?: boolean
    className?: string
    /** Texto de apoio abaixo do campo (ex: o motivo de ele estar travado). */
    hint?: string
    /**
     * Preencher sozinho com o convênio cadastrado do paciente.
     *
     * Só faz sentido em CADASTRO. Na edição o valor vem do registro, e
     * sobrescrevê-lo aqui trocaria o pagador do atendimento sem ninguém pedir —
     * o operador abriria a tela para corrigir a data e salvaria um convênio.
     */
    autoSelectFromClient?: boolean
}

export function HealthPlanSelector({
    value,
    onChange,
    clientId,
    label = 'Convênio',
    placeholder = 'Selecione um convênio (opcional)',
    disabled = false,
    className = '',
    hint,
    autoSelectFromClient = true,
}: Props) {
    const { user } = useAuth()
    const [plans, setPlans] = useState<HealthPlan[]>([])
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDropdown, setShowDropdown] = useState(false)
    const [selectedPlan, setSelectedPlan] = useState<HealthPlan | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const autoSelectedRef = useRef(false)

    const loadPlans = useCallback(async () => {
        if (!user?.organizationId) return
        setLoading(true)
        try {
            const response = await getHealthPlans(user.organizationId)
            if (response.success) {
                const list = Array.isArray(response.result)
                    ? response.result.filter(p => p.active)
                    : []
                setPlans(list)
            }
        } catch {
            // silencioso
        } finally {
            setLoading(false)
        }
    }, [user?.organizationId])

    // Carregar planos ao montar
    useEffect(() => {
        loadPlans()
    }, [loadPlans])

    // Buscar plano selecionado pelo value
    useEffect(() => {
        if (value && !selectedPlan && plans.length > 0) {
            const found = plans.find(p => p.id === value)
            if (found) setSelectedPlan(found)
        } else if (!value) {
            setSelectedPlan(null)
        }
    }, [value, plans])

    // Auto-seleção baseada no convênio do paciente
    useEffect(() => {
        if (
            !clientId ||
            plans.length === 0 ||
            !autoSelectFromClient ||
            disabled
        ) {
            autoSelectedRef.current = false
            return
        }

        autoSelectedRef.current = false

        getClientHealthPlans(clientId)
            .then(response => {
                if (response.success) {
                    const clientPlans = Array.isArray(response.result)
                        ? response.result
                        : []
                    if (clientPlans.length > 0) {
                        const activePlan =
                            clientPlans.find(cp => {
                                if (
                                    cp.validUntil &&
                                    new Date(cp.validUntil) < new Date()
                                )
                                    return false
                                return plans.some(p => p.id === cp.healthPlanId)
                            }) || clientPlans[0]

                        if (
                            activePlan &&
                            plans.some(p => p.id === activePlan.healthPlanId)
                        ) {
                            autoSelectedRef.current = true
                            onChange(activePlan.healthPlanId)
                        }
                    }
                }
            })
            .catch(() => {})
    }, [clientId, plans, autoSelectFromClient, disabled])

    // Click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setShowDropdown(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const filtered = searchTerm
        ? plans.filter(
              p =>
                  p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  (p.ansCode && p.ansCode.includes(searchTerm))
          )
        : plans

    const handleSelect = (plan: HealthPlan) => {
        setSelectedPlan(plan)
        onChange(plan.id)
        setShowDropdown(false)
        setSearchTerm('')
    }

    const handleClear = () => {
        setSelectedPlan(null)
        onChange(null)
        setSearchTerm('')
    }

    return (
        <div className={`form-control w-full ${className}`} ref={containerRef}>
            {label && (
                <label className="label">
                    <span className="label-text">{label}</span>
                </label>
            )}

            <div className="relative">
                {selectedPlan ? (
                    <div className="flex items-center gap-2 input input-bordered w-full pr-10">
                        <MdHealthAndSafety className="text-lg text-secondary flex-shrink-0" />
                        <span className="truncate">
                            {selectedPlan.name}
                            {selectedPlan.ansCode && (
                                <span className="text-base-content/50 ml-1">
                                    (ANS: {selectedPlan.ansCode})
                                </span>
                            )}
                        </span>
                        {!disabled && (
                            <button
                                type="button"
                                onClick={handleClear}
                                className="absolute right-3 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs btn-circle"
                            >
                                <MdClose className="text-lg" />
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                        <input
                            type="text"
                            placeholder={placeholder}
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => {
                                setSearchTerm(e.target.value)
                                setShowDropdown(true)
                            }}
                            onFocus={() => {
                                if (!disabled) setShowDropdown(true)
                            }}
                            disabled={disabled}
                            autoComplete="off"
                            aria-autocomplete="none"
                        />
                    </>
                )}

                {showDropdown && !selectedPlan && (
                    <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {loading ? (
                            <div className="p-4 text-center">
                                <span className="loading loading-spinner loading-sm"></span>
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="p-4 text-center text-base-content/60 text-sm">
                                {searchTerm
                                    ? 'Nenhum convênio encontrado'
                                    : 'Nenhum convênio cadastrado'}
                            </div>
                        ) : (
                            filtered.map(plan => (
                                <button
                                    key={plan.id}
                                    type="button"
                                    className="w-full px-4 py-3 text-left hover:bg-base-200 flex items-center gap-3 border-b border-base-200 last:border-b-0"
                                    onClick={() => handleSelect(plan)}
                                >
                                    <div className="w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                                        <MdHealthAndSafety className="text-secondary" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="font-medium truncate">
                                            {plan.name}
                                        </p>
                                        {plan.ansCode && (
                                            <p className="text-xs text-base-content/60">
                                                ANS: {plan.ansCode}
                                            </p>
                                        )}
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                )}
            </div>

            {hint && (
                <label className="label">
                    <span className="label-text-alt text-base-content/60">
                        {hint}
                    </span>
                </label>
            )}
        </div>
    )
}
